import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Grid, Paper, Stack, Typography } from "@mui/material";
import AppLoader from '../../../components/AppLoader/AppLoader';
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useSelector } from "react-redux";
import XLSX, { boldRows } from "utils/xlsxStyled";
import eOpsLogo from "../../../images/eops.png";
import { cfpBaseUrl } from "../../../network/urls";
import ReportExportButtons from "../../Report/components/ReportExportButtons";
import "../../Report/reportPageLayout.scss";
import "../../Report/ChangeOfAttributesStatusReport.scss";
import "./UserComplaintProcessingStatistics.scss";
import {
  convertDatewithTime,
  getName,
  companyShortName,
  companyName,
  getLogo,
} from "../../../services/Functions";
import "../../../style/common.scss";
import { resolveOfficeCodeForAdmin, resolveReportGoOfficeCode } from "../../../utils/adminCompany";

const TABLE_COLUMNS = [
  "User ID",
  "Full Name",
  "Role Name",
  "Circle Code",
  "Division Code",
  "Sub-Division Code",
  "Current Month Complaints",
];

const getCompanies = (companyCode) => {
  switch (companyCode) {
    case "10":
      return "Power Information Technology Company";
    case "11":
      return "Lahore Electric Supply Company";
    case "12":
      return "Gujranwala Electric Power Company";
    case "13":
      return "Faisalabad Electric Supply Company";
    case "14":
      return "Islamabad Electric Supply Company";
    case "15":
      return "Multan Electric Power Company";
    case "26":
      return "Peshawar Electric Supply Company";
    case "27":
      return "Hazara Electric Supply Company";
    case "38":
      return "Sukkur Electric Power Company";
    case "37":
      return "Hyderabad Electric Supply Company";
    case "48":
      return "Quetta Electric Supply Company";
    case "59":
      return "Tribal Electric Supply Company";
    default:
    return "";
  }
};

const normalizeRow = (item) => ({
  reportMonth: item.ReportMonth ?? item.reportMonth ?? "-",
  userId: item.UserId ?? item.userId ?? "-",
  fullName: String(item.FullName ?? item.fullName ?? "-").trim(),
  roleName: item.RoleName ?? item.roleName ?? "-",
  circleCode: item.CIRCLECODE ?? item.circleCode ?? "-",
  divisionCode: item.DIVISIONCODE ?? item.divisionCode ?? "-",
  subDivisionCode: item.SUBDIVISIONCODE ?? item.subDivisionCode ?? "-",
  currentMonthComplaints: Number(
    item.CurrentMonthComplaints ?? item.currentMonthComplaints ?? 0
  ),
});

const extractComplaintSummaryRows = (result) => {
  if (!result) return [];
  if (Array.isArray(result.data)) return result.data;
  if (Array.isArray(result.message)) return result.message;
  if (Array.isArray(result?.data?.records)) return result.data.records;
  return [];
};

const isApiSuccess = (result) =>
  result?.status === "200" || result?.status === 200;

const UserComplaintProcessingStatistics = () => {
  const {
    token,
    code,
    name,
    roleName,
    roleTypeName,
    cscID,
    cfcLevelId,
    hierarchy: userHierarchy,
    selectedAdminCompanyCode,
  } = useSelector((state) => state.auth);

  const offCode =
    userHierarchy == 1
    ? code
    : userHierarchy == 2
      ? code.slice(0, 2)
      : userHierarchy == 3
        ? code.slice(0, 3)
        : userHierarchy == 4
          ? code.slice(0, 4)
      : userHierarchy == 5
            ? code.slice(0, 5)
            : code;

  const [viewBackdrop, setViewBackdrop] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [subDivisions, setSubDivisions] = useState(
    JSON.parse(window.localStorage.getItem("subDivisions")) || []
  );
  const [selectedCompanyCode, setSelectedCompanyCode] = useState("");
  const tableRef = useRef(null);
  const [selectedDivision, setSelectedDivision] = useState("");
  const [selectedSubDivision, setSelectedSubDivision] = useState("");
  const [filteredDivisions, setFilteredDivisions] = useState([]);
  const [filteredSubDivisions, setFilteredSubDivisions] = useState([]);
const [isGoDisabled, setIsGoDisabled] = useState(true); 
  const [lastAppliedFilters, setLastAppliedFilters] = useState(null);
  const [apiData, setApiData] = useState([]);
  const [circleCode, setCircleCode] = useState("");
  const [circleName, setCircleName] = useState("");
  const [officeCode, setOfficeCode] = useState("");
  const [officeName, setOfficeName] = useState("");
  const [reportOfficeName, setReportOfficeName] = useState("");
  const [hierarchy, setHierarchy] = useState("");
  const [filteredCircles, setFilteredCircles] = useState([]);
  const [companyCode, setCompanyCode] = useState("15");

  useEffect(() => {
    if (String(code) !== "10" || !selectedAdminCompanyCode) return;
    setSelectedCompanyCode((prev) => prev || String(selectedAdminCompanyCode));
    setOfficeCode((prev) => {
      if (prev && String(prev) !== "10") return prev;
      return String(selectedAdminCompanyCode);
    });
    setOfficeName((prev) => {
      if (prev) return prev;
      return (
        companyShortName(String(selectedAdminCompanyCode)) ||
        getCompanies(String(selectedAdminCompanyCode))
      );
    });
  }, [code, selectedAdminCompanyCode]);

  useEffect(() => {
    if (circleCode !== "" && circleCode != null) {
      setOfficeCode(circleCode);
    }
  }, [circleCode]);

  useEffect(() => {
    if (code == 10 && selectedCompanyCode) {
      const fetchSubDivisions = async () => {
        setViewBackdrop(true);
        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);
        const formdata = new FormData();
        formdata.append("companyCode", selectedCompanyCode);
        const requestOptions = {
          method: "POST",
          headers: myHeaders,
          body: formdata,
          redirect: "follow",
        };
        try {
          const response = await fetch(
            cfpBaseUrl + "customerFaciliationPortal/subDivWithCompDetails",
            requestOptions
          );
          if (response.ok) {
            const result = await response.json();
            if (result.status === "200") {
              setSubDivisions(result.message);
              setApiData(result.message);
              const circles = result.message.reduce((acc, curr) => {
                if (!acc.some((item) => item.circleCode === curr.circleCode)) {
                  acc.push({
                    circleCode: curr.circleCode,
                    circleName: curr.circleName,
                    companyCode: curr.companyCode,
                  });
                }
                return acc;
              }, []);
              setFilteredCircles(circles);
            } else {
              setSubDivisions([]);
              setFilteredCircles([]);
            }
          } else {
            throw new Error(`Request failed with status ${response.status}`);
          }
        } catch (error) {
          setSubDivisions([]);
          setFilteredCircles([]);
        } finally {
          setViewBackdrop(false);
        }
      };
      fetchSubDivisions();
    } else if (code != 10) {
      const circles = subDivisions.reduce((acc, curr) => {
        if (!acc.some((item) => item.circleCode === curr.circleCode)) {
          acc.push({
            circleCode: curr.circleCode,
            circleName: curr.circleName,
            companyCode: curr.companyCode,
          });
        }
        return acc;
      }, []);
      setFilteredCircles(circles);
    }
  }, [code, selectedCompanyCode, token]);

  const clearTableOnFilterChange = () => {
    setTableData([]);
    setReportOfficeName("");
  };

  const handleChangeCompany = async (e) => {
    clearTableOnFilterChange();
    const selectedCompany = e.target.value;
    setSelectedCompanyCode(selectedCompany);
    setOfficeCode(selectedCompany);
    setOfficeName(getCompanies(selectedCompany) || "");

    if (code == 10 && selectedCompany) {
      setViewBackdrop(true);
      const myHeaders = new Headers();
      myHeaders.append("Authorization", token);
      const formdata = new FormData();
      formdata.append("companyCode", selectedCompany);
      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: formdata,
        redirect: "follow",
      };

      try {
        const response = await fetch(
          cfpBaseUrl + "customerFaciliationPortal/subDivWithCompDetails",
          requestOptions
        );
        if (response.ok) {
          const result = await response.json();
          if (result.status === "200") {
            setSubDivisions(result.message);
            setApiData(result.message);
            setCircleCode("");
            setSelectedDivision("");
            setSelectedSubDivision("");
            setCircleName("");
            setFilteredDivisions([]);
            setFilteredSubDivisions([]);
            const circles = result.message.reduce((acc, curr) => {
              if (!acc.some((item) => item.circleCode === curr.circleCode)) {
                acc.push({
                  circleCode: curr.circleCode,
                  circleName: curr.circleName,
                  companyCode: curr.companyCode,
                });
              }
              return acc;
            }, []);
            setFilteredCircles(circles);
          } else {
            setSubDivisions([]);
            setFilteredCircles([]);
          }
        } else {
          throw new Error(`Request failed with status ${response.status}`);
        }
      } catch (error) {
        setSubDivisions([]);
        setFilteredCircles([]);
      } finally {
        setViewBackdrop(false);
      }
    } else if (!selectedCompany) {
      setOfficeName("PITC");
      setCircleCode("");
      setSelectedDivision("");
      setSelectedSubDivision("");
      setCircleName("");
      setFilteredDivisions([]);
      setFilteredSubDivisions([]);
      setFilteredCircles([]);
      setSubDivisions([]);
      setApiData([]);
    }
  };

  const handleCircleChange = async (event) => {
    clearTableOnFilterChange();
    const selectedCircleCode = event.target.value
      ? parseInt(event.target.value)
      : "";
    if (code == 10 && selectedCircleCode && selectedCompanyCode) {
      setViewBackdrop(true);
      const myHeaders = new Headers();
      myHeaders.append("Authorization", token);
      const formdata = new FormData();
      formdata.append("companyCode", selectedCompanyCode);
      formdata.append("circleCode", selectedCircleCode);
      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: formdata,
        redirect: "follow",
      };

      try {
        const response = await fetch(
          cfpBaseUrl + "customerFaciliationPortal/subDivWithCompDetails",
          requestOptions
        );
        if (response.ok) {
          const result = await response.json();
          if (result.status === "200") {
            setSubDivisions(result.message);
            setApiData(result.message);
            setCircleCode(selectedCircleCode);
            setOfficeCode(selectedCircleCode);
            const circle = filteredCircles.find(
              (item) => item.circleCode === selectedCircleCode
            );
            const nextCircleName = circle?.circleName || "";
            setOfficeName(nextCircleName.concat(" Circle"));
            setCircleName(nextCircleName);
            setSelectedDivision("");
            setSelectedSubDivision("");
            setFilteredSubDivisions([]);
            const uniqueDivisions = result.message
              .filter((item) => item.circleCode === selectedCircleCode)
              .reduce((acc, item) => {
                if (
                  !acc.some(
                    (division) => division.divisionCode === item.divisionCode
                  )
                ) {
                  acc.push({
                    divisionCode: item.divisionCode,
                    divisionName: item.divisionName,
                    companyCode: item.companyCode,
                  });
                }
                return acc;
              }, []);
            setFilteredDivisions(uniqueDivisions);
          } else {
            setSubDivisions([]);
            setFilteredDivisions([]);
          }
        } else {
          throw new Error(`Request failed with status ${response.status}`);
        }
      } catch (error) {
        setSubDivisions([]);
        setFilteredDivisions([]);
      } finally {
        setViewBackdrop(false);
      }
    } else if (code == 10 && !selectedCircleCode && selectedCompanyCode) {
      setViewBackdrop(true);
      const myHeaders = new Headers();
      myHeaders.append("Authorization", token);
      const formdata = new FormData();
      formdata.append("companyCode", selectedCompanyCode);
      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: formdata,
        redirect: "follow",
      };

      try {
        const response = await fetch(
          cfpBaseUrl + "customerFaciliationPortal/subDivWithCompDetails",
          requestOptions
        );
        if (response.ok) {
          const result = await response.json();
          if (result.status === "200") {
            setSubDivisions(result.message);
            setApiData(result.message);
            setCircleCode("");
            setOfficeCode(selectedCompanyCode);
            setOfficeName(getCompanies(selectedCompanyCode));
            setCircleName("");
            setSelectedDivision("");
            setSelectedSubDivision("");
            setFilteredSubDivisions([]);
            const uniqueDivisions = result.message.reduce((acc, item) => {
              if (
                !acc.some(
                  (division) => division.divisionCode === item.divisionCode
                )
              ) {
                acc.push({
                  divisionCode: item.divisionCode,
                  divisionName: item.divisionName,
                  companyCode: item.companyCode,
                });
              }
              return acc;
            }, []);
            setFilteredDivisions(uniqueDivisions);
          } else {
            setSubDivisions([]);
            setFilteredDivisions([]);
          }
        } else {
          throw new Error(`Request failed with status ${response.status}`);
        }
      } catch (error) {
        setSubDivisions([]);
        setFilteredDivisions([]);
      } finally {
        setViewBackdrop(false);
      }
    } else if (selectedCircleCode) {
      setCircleCode(selectedCircleCode);
      setOfficeCode(selectedCircleCode);
      const circle = filteredCircles.find(
        (item) => item.circleCode === selectedCircleCode
      );
      const nextCircleName = circle?.circleName || "";
      setOfficeName(nextCircleName.concat(" Circle"));
      setCircleName(nextCircleName);
      setSelectedDivision("");
      setSelectedSubDivision("");
      setFilteredSubDivisions([]);
      const uniqueDivisions = subDivisions
        .filter((item) => item.circleCode === selectedCircleCode)
        .reduce((acc, item) => {
          if (
            !acc.some((division) => division.divisionCode === item.divisionCode)
          ) {
            acc.push({
              divisionCode: item.divisionCode,
              divisionName: item.divisionName,
              companyCode: item.companyCode,
            });
          }
          return acc;
        }, []);
      setFilteredDivisions(uniqueDivisions);
    } else {
      setCircleCode("");
      setSelectedDivision("");
      setSelectedSubDivision("");
      setFilteredSubDivisions([]);
      if (roleTypeName == "Technical") {
        if (offCode) {
          setOfficeCode(resolveOfficeCodeForAdmin(offCode, selectedAdminCompanyCode));
          const nextOfficeName = getName(
            String(resolveOfficeCodeForAdmin(offCode, selectedAdminCompanyCode)),
            subDivisions
          );
          setOfficeName(nextOfficeName.concat(" Region"));
          setCircleName("");
          const circles = subDivisions.reduce((acc, curr) => {
            if (!acc.some((item) => item.circleCode === curr.circleCode)) {
              acc.push({
                circleCode: curr.circleCode,
                circleName: curr.circleName,
                companyCode: curr.companyCode,
              });
            }
            return acc;
          }, []);
          setFilteredCircles(circles);
          const uniqueDivisions = subDivisions.reduce((acc, item) => {
            if (
              !acc.some(
                (division) => division.divisionCode === item.divisionCode
              )
            ) {
              acc.push({
                divisionCode: item.divisionCode,
                divisionName: item.divisionName,
                companyCode: item.companyCode,
              });
            }
            return acc;
          }, []);
          setFilteredDivisions(uniqueDivisions);
        }
      } else if (roleTypeName == "Commercial") {
        if (cfcLevelId == 2) {
          if (offCode) {
            setOfficeCode(offCode.toString().slice(0, 2));
            setOfficeName(cscID);
            setHierarchy(cfcLevelId);
            const uniqueDivisions = subDivisions.reduce((acc, item) => {
              if (
                !acc.some(
                  (division) => division.divisionCode === item.divisionCode
                )
              ) {
                acc.push({
                  divisionCode: item.divisionCode,
                  divisionName: item.divisionName,
                  companyCode: item.companyCode,
                });
              }
              return acc;
            }, []);
            setFilteredDivisions(uniqueDivisions);
          }
        } else if (cfcLevelId == 3) {
          if (offCode) {
            setOfficeCode(offCode.toString().slice(0, 3));
            setOfficeName(cscID);
            setHierarchy(cfcLevelId);
            const uniqueDivisions = subDivisions.reduce((acc, item) => {
              if (
                !acc.some(
                  (division) => division.divisionCode === item.divisionCode
                )
              ) {
                acc.push({
                  divisionCode: item.divisionCode,
                  divisionName: item.divisionName,
                  companyCode: item.companyCode,
                });
              }
              return acc;
            }, []);
            setFilteredDivisions(uniqueDivisions);
          }
        }
      }
    }
  };

  const handleDivisionChange = async (e) => {
    clearTableOnFilterChange();
    const selectedDivisionCode = parseInt(e.target.value);
    if (code == 10 && selectedDivisionCode && selectedCompanyCode) {
      setViewBackdrop(true);
      const myHeaders = new Headers();
      myHeaders.append("Authorization", token);
      const formdata = new FormData();
      formdata.append("companyCode", selectedCompanyCode);
      formdata.append("divisionCode", selectedDivisionCode);
      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: formdata,
        redirect: "follow",
      };

      try {
        const response = await fetch(
          cfpBaseUrl + "customerFaciliationPortal/subDivWithCompDetails",
          requestOptions
        );
        if (response.ok) {
          const result = await response.json();
          if (result.status === "200") {
            setSubDivisions(result.message);
            setApiData(result.message);
            setSelectedDivision(selectedDivisionCode);
            setOfficeCode(selectedDivisionCode);
            const division = filteredDivisions.find(
              (item) => item.divisionCode === selectedDivisionCode
            );
            const divisionName = division?.divisionName || "";
            setOfficeName(divisionName.concat(" Division"));
            setSelectedSubDivision("");
            const uniqueSubDivisions = result.message
              .filter((item) => item.divisionCode == selectedDivisionCode)
              .reduce((acc, item) => {
                if (
                  !acc.some(
                    (sub) => sub.subDivisionCode === item.subDivisionCode
                  )
                ) {
                  acc.push({
                    subDivisionCode: item.subDivisionCode,
                    subDivisionName: item.subDivisionName,
                    companyCode: item.companyCode,
                  });
                }
                return acc;
              }, []);
            setFilteredSubDivisions(uniqueSubDivisions);
          } else {
            setSubDivisions([]);
            setFilteredSubDivisions([]);
          }
        } else {
          throw new Error(`Request failed with status ${response.status}`);
        }
      } catch (error) {
        setSubDivisions([]);
        setFilteredSubDivisions([]);
      } finally {
        setViewBackdrop(false);
      }
    } else if (selectedDivisionCode) {
      setSelectedDivision(selectedDivisionCode);
      setOfficeCode(selectedDivisionCode);
      const divisionName =
        filteredDivisions.find(
          (division) => division.divisionCode === selectedDivisionCode
        )?.divisionName || "";
      setOfficeName(divisionName.concat(" Division"));
      setSelectedSubDivision("");
      const uniqueSubDivisions = subDivisions
        .filter((item) => item.divisionCode == selectedDivisionCode)
        .reduce((acc, item) => {
          if (
            !acc.some((sub) => sub.subDivisionCode === item.subDivisionCode)
          ) {
            acc.push({
              subDivisionCode: item.subDivisionCode,
              subDivisionName: item.subDivisionName,
              companyCode: item.companyCode,
            });
          }
          return acc;
        }, []);
      setFilteredSubDivisions(uniqueSubDivisions);
    } else {
      setSelectedDivision("");
      setOfficeCode(circleCode ? circleCode : offCode);
      const nextOfficeName = getName(
        circleCode ? circleCode.toString() : offCode.toString(),
        subDivisions
      );
      setOfficeName(nextOfficeName.concat(" Circle"));
      setSelectedSubDivision("");
      setFilteredSubDivisions([]);
      const uniqueDivisions = subDivisions
        .filter(
          (item) => item.circleCode == (circleCode ? circleCode : offCode)
        )
        .reduce((acc, item) => {
          if (
            !acc.some((division) => division.divisionCode === item.divisionCode)
          ) {
            acc.push({
              divisionCode: item.divisionCode,
              divisionName: item.divisionName,
              companyCode: item.companyCode,
            });
          }
          return acc;
        }, []);
      setFilteredDivisions(uniqueDivisions);
    }
  };

  const handleSubDivisionChange = async (e) => {
    clearTableOnFilterChange();
    const selectedSubDivisionCode = parseInt(e.target.value);
    if (code == 10 && selectedSubDivisionCode && selectedCompanyCode) {
      setViewBackdrop(true);
      const myHeaders = new Headers();
      myHeaders.append("Authorization", token);
      const formdata = new FormData();
      formdata.append("companyCode", selectedCompanyCode);
      formdata.append("subDivisionCode", selectedSubDivisionCode);
      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: formdata,
        redirect: "follow",
      };

      try {
        const response = await fetch(
          cfpBaseUrl + "customerFaciliationPortal/subDivWithCompDetails",
          requestOptions
        );
        if (response.ok) {
          const result = await response.json();
          if (result.status === "200") {
            setSubDivisions(result.message);
            setApiData(result.message);
            setSelectedSubDivision(selectedSubDivisionCode);
            setOfficeCode(selectedSubDivisionCode);
            const subDivision = filteredSubDivisions.find(
              (sub) => sub.subDivisionCode === selectedSubDivisionCode
            );
            const subDivisionName = subDivision?.subDivisionName || "";
            setOfficeName(subDivisionName.concat(" Sub Division"));
          } else {
            setSubDivisions([]);
          }
        } else {
          throw new Error(`Request failed with status ${response.status}`);
        }
      } catch (error) {
        setSubDivisions([]);
      } finally {
        setViewBackdrop(false);
      }
    } else if (selectedSubDivisionCode) {
      setSelectedSubDivision(selectedSubDivisionCode);
      setOfficeCode(selectedSubDivisionCode);
      const subDivisionName =
        filteredSubDivisions.find(
          (sub) => sub.subDivisionCode === selectedSubDivisionCode
        )?.subDivisionName || "";
      setOfficeName(subDivisionName.concat(" Sub Division"));
    } else {
      setSelectedSubDivision("");
      const nextOfficeName = getName(
        selectedDivision ? selectedDivision.toString() : offCode.toString(),
        subDivisions
      );
      setOfficeName(nextOfficeName.concat(" Division"));
      setOfficeCode(selectedDivision ? selectedDivision : offCode);
    }
  };

  useEffect(() => {
    if (roleTypeName == "Technical") {
      if (offCode) {
        const effectiveOfficeCode =
          String(code) === "10" && selectedCompanyCode && selectedCompanyCode !== "10"
            ? selectedCompanyCode
            : resolveOfficeCodeForAdmin(offCode, selectedAdminCompanyCode);
        setOfficeCode(effectiveOfficeCode);
        const offName = getName(String(effectiveOfficeCode), subDivisions);
        setOfficeName(offName);
        setHierarchy(offCode.toString().length);
      }
    } else if (roleTypeName == "Commercial") {
      if (cfcLevelId == 1) {
        if (offCode) {
          if (offCode == 10) {
            const effectiveCompany =
              selectedCompanyCode || selectedAdminCompanyCode || offCode;
            setOfficeCode(effectiveCompany);
            setOfficeName(getCompanies(String(effectiveCompany)) || "PITC");
          } else {
            setOfficeCode(offCode);
            const offName = getName(offCode, subDivisions);
            setOfficeName(offName);
          }
          setHierarchy(offCode.length);
        }
      } else if (cfcLevelId == 2) {
        if (offCode) {
          setOfficeCode(offCode.toString().slice(0, 2));
          setOfficeName(cscID);
          setHierarchy(cfcLevelId);
        }
      } else if (cfcLevelId == 3) {
        if (offCode) {
          setOfficeCode(offCode.toString().slice(0, 3));
          setOfficeName(cscID);
          setHierarchy(cfcLevelId);
        }
      } else if (cfcLevelId == 4) {
                if (offCode) {
                    setOfficeCode(offCode.toString().slice(0, 4));
          setOfficeName(cscID);
                    setHierarchy(cfcLevelId);
                }
      } else if (cfcLevelId == 5) {
                if (offCode) {
                    setOfficeCode(offCode.toString().slice(0, 5));
          setOfficeName(cscID);
                    setHierarchy(cfcLevelId);
                }
            }
    }
  }, [offCode, cfcLevelId, roleTypeName, cscID, selectedAdminCompanyCode, selectedCompanyCode]);

  useEffect(() => {
    if (hierarchy) {
      if (hierarchy == 1 || cfcLevelId === 1) {
        const circles = subDivisions.reduce((acc, curr) => {
          if (!acc.some((item) => item.circleCode === curr.circleCode)) {
            acc.push({
              circleCode: curr.circleCode,
              circleName: curr.circleName,
              companyCode: curr.companyCode,
            });
          }
          return acc;
        }, []);
        setFilteredCircles(circles);
      } else if (hierarchy == 2 || cfcLevelId == 2) {
        const circles = subDivisions.reduce((acc, curr) => {
          if (!acc.some((item) => item.circleCode === curr.circleCode)) {
            acc.push({
              circleCode: curr.circleCode,
              circleName: curr.circleName,
              companyCode: curr.companyCode,
            });
          }
          return acc;
        }, []);
        setFilteredCircles(circles);
      } else if (hierarchy == 3 || cfcLevelId == 3) {
        setCircleCode(offCode.slice(0, 3));
        const uniqueDivisions = subDivisions
          .filter((item) => item.circleCode == offCode.slice(0, 3))
          .reduce((acc, item) => {
            if (
              !acc.some(
                (division) => division.divisionCode === item.divisionCode
              )
            ) {
              acc.push({
                divisionCode: item.divisionCode,
                divisionName: item.divisionName,
                companyCode: item.companyCode,
              });
            }
            return acc;
          }, []);
        setFilteredDivisions(uniqueDivisions);
      } else if (
        hierarchy == 4 ||
        cfcLevelId == 4 ||
        hierarchy == 5 ||
        cfcLevelId == 5
      ) {
        setSelectedDivision(offCode.slice(0, 4));
        const uniqueSubDivisions = subDivisions
          .filter((item) => item.divisionCode == offCode.slice(0, 4))
          .reduce((acc, item) => {
            if (
              !acc.some((sub) => sub.subDivisionCode === item.subDivisionCode)
            ) {
              acc.push({
                subDivisionCode: item.subDivisionCode,
                subDivisionName: item.subDivisionName,
                companyCode: item.companyCode,
              });
            }
            return acc;
          }, []);
        setFilteredSubDivisions(uniqueSubDivisions);
      }
    }
  }, [hierarchy, cfcLevelId, subDivisions]);

  const getComplaintSummary = async (apiOfficeCode) => {
    const resolvedOfficeCode = String(apiOfficeCode || "").trim();
    if (!resolvedOfficeCode || resolvedOfficeCode === "10") {
      setTableData([]);
      setViewBackdrop(false);
      return;
    }

    setViewBackdrop(true);
    try {
      const myHeaders = new Headers();
      myHeaders.append("Authorization", token);
      myHeaders.append("Content-Type", "application/json");

      const raw = JSON.stringify({
        officeCode: resolvedOfficeCode,
      });

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow",
      };

      const response = await fetch(
        cfpBaseUrl + "lineComplaints/getCurrentMonthUserComplaintSummary",
        requestOptions
      );
      const result = await response.json();
      const rows = extractComplaintSummaryRows(result);

      if (response.ok && isApiSuccess(result) && rows.length > 0) {
        setTableData(rows.map(normalizeRow));
    } else {
        setTableData([]);
      }
    } catch (error) {
      setTableData([]);
    }
    setViewBackdrop(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { day: "2-digit", month: "short", year: "numeric" };
    return date.toLocaleDateString("en-GB", options).replace(/ /g, "-");
  };

  const resolveReportOfficeName = () => {
    if (selectedSubDivision) {
      const subDivision = filteredSubDivisions.find(
        (item) => String(item.subDivisionCode) === String(selectedSubDivision)
      );
      if (subDivision?.subDivisionName) {
        return `${subDivision.subDivisionName} Sub Division`;
      }
      return String(selectedSubDivision);
    }
    if (selectedDivision) {
      const division = filteredDivisions.find(
        (item) => String(item.divisionCode) === String(selectedDivision)
      );
      if (division?.divisionName) {
        return `${division.divisionName} Division`;
      }
      return String(selectedDivision);
    }
    if (circleCode) {
      const circle = filteredCircles.find(
        (item) => String(item.circleCode) === String(circleCode)
      );
      if (circle?.circleName) {
        return `${circle.circleName} Circle`;
      }
      return String(circleCode);
    }

    const companyCodeValue =
      String(code) === "10"
        ? selectedCompanyCode || selectedAdminCompanyCode || officeCode
        : String(officeCode || code).slice(0, 2);

    if (companyCodeValue && String(companyCodeValue) !== "10") {
      return companyShortName(String(companyCodeValue)) || getCompanies(String(companyCodeValue));
    }

    if (officeName) {
      return officeName;
    }

    return companyShortName(String(code).slice(0, 2)) || getCompanies(String(code).slice(0, 2));
  };

  const getReportTitle = (officeLabel = reportOfficeName || resolveReportOfficeName()) => {
    const monthLabel = tableData[0]?.reportMonth || "";
    const officePart = officeLabel ? ` for ${officeLabel}` : "";
    const monthPart = monthLabel ? ` (${monthLabel})` : "";
    return `User Complaint Processing Statistics${officePart}${monthPart}`;
  };

  const getExportFileBaseName = () => {
    const officeLabel = reportOfficeName || resolveReportOfficeName() || "report";
    return `${formatDate(new Date())} - ${officeLabel} - user-complaint-processing-statistics`;
  };

  const exportToExcel = () => {
    const title = getReportTitle();
    const headers = TABLE_COLUMNS;
    const dataRows = tableData.map((item) => [
      item.userId,
      item.fullName,
      item.roleName,
      item.circleCode,
      item.divisionCode,
      item.subDivisionCode,
      item.currentMonthComplaints,
    ]);
    const totalRow = [
    "Total",
      "",
      "",
      "",
      "",
      "",
      tableData.reduce((sum, item) => sum + (item.currentMonthComplaints || 0), 0),
    ];

    const ws = XLSX.utils.aoa_to_sheet([[title], headers, ...dataRows, totalRow]);
    ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } }];
    boldRows(ws, 1, 1);
    if (dataRows.length > 0) {
      boldRows(ws, dataRows.length + 2, dataRows.length + 2);
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout]), `${getExportFileBaseName()}.xlsx`);
  };

  const DownloadPdf = async (print) => {
    return new Promise((resolve) => {
      const title = getReportTitle();
      const officeLabel = reportOfficeName || resolveReportOfficeName();

      const pdf = new jsPDF("landscape", "mm", "a4", { compressPdf: true });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const logoWidth = ["12", "13", "11", "16", "27", "48", "59"].includes(
        companyCode
      )
        ? 25
        : 30;
      const logoHeight = ["12", "13", "11", "16", "27", "48", "59"].includes(
        companyCode
      )
        ? 20
        : 10;
      pdf.addImage(
        getLogo(code.slice(0, 2)),
        "PNG",
        15,
        5,
        logoWidth,
        logoHeight
      );

      const eOpsLogoWidth = 20;
      const eOpsLogoHeight = 8;
      const eOpsLogoXPosition = [
        "12",
        "13",
        "11",
        "16",
        "27",
        "48",
        "59",
      ].includes(companyCode)
        ? 43
        : 17;
      pdf.addImage(
        eOpsLogo,
        "PNG",
        eOpsLogoXPosition,
        ["12", "13", "11", "16", "27", "48", "59"].includes(companyCode)
          ? 12
          : 17,
        eOpsLogoWidth,
        eOpsLogoHeight
      );

      pdf.setFontSize(12);
      pdf.text(companyName(code.slice(0, 2)), pageWidth / 2, 12, {
        align: "center",
      });
      pdf.text(
        `User Complaint Processing Statistics${officeLabel ? ` for ${officeLabel}` : ""}`,
        pageWidth / 2,
        18,
        { align: "center" }
      );
      pdf.setFontSize(10);
      pdf.text(title, pageWidth / 2, 25, { align: "center" });
      pdf.text(convertDatewithTime(new Date()), pageWidth - 65, 12);
      pdf.text("Name: " + name, pageWidth - 65, 17);
      pdf.text("Designation: " + roleName, pageWidth - 65, 22);

      const headers = TABLE_COLUMNS.map((column) => ({
        content: column,
        styles: { halign: column === "Current Month Complaints" ? "center" : "left" },
      }));

      let bodyData = [];
      if (tableData.length > 0) {
        bodyData = tableData.map((item) => [
          { content: item.userId, styles: { halign: "left" } },
          { content: item.fullName, styles: { halign: "left" } },
          { content: item.roleName, styles: { halign: "left" } },
          { content: item.circleCode, styles: { halign: "center" } },
          { content: item.divisionCode, styles: { halign: "center" } },
          { content: item.subDivisionCode, styles: { halign: "center" } },
          {
            content: item.currentMonthComplaints,
            styles: { halign: "center" },
          },
        ]);

        bodyData.push([
          {
            content: "Total",
            colSpan: 6,
            styles: {
              halign: "left",
              fontStyle: "bold",
              fillColor: [255, 245, 192],
            },
          },
          {
            content: tableData.reduce(
              (sum, item) => sum + (item.currentMonthComplaints || 0),
              0
            ),
            styles: {
              halign: "center",
              fontStyle: "bold",
              fillColor: [255, 245, 192],
            },
          },
        ]);
      } else {
        bodyData.push([
          {
            content: "No Data Found",
            colSpan: TABLE_COLUMNS.length,
            styles: {
              halign: "center",
              fontSize: 12,
              fontStyle: "bold",
              textColor: [255, 0, 0],
            },
          },
        ]);
      }

      pdf.autoTable({
        head: [headers],
        body: bodyData,
        startY: 32,
        theme: "grid",
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: {
          fillColor: [0, 106, 209],
          textColor: [255, 255, 255],
          fontStyle: "bold",
        },
        margin: { left: 10, right: 10 },
      });

      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth - 45, pageHeight - 10);
      }

      const filename = `${getExportFileBaseName()}.pdf`;
      if (print) {
        pdf.save(filename);
      }
      resolve(pdf);
    });
  };

  const printPDF = async () => {
    const pdf = await DownloadPdf(false);
    const pdfUrl = pdf.output("bloburl");
    const iframe = document.createElement("iframe");
    iframe.style.position = "absolute";
    iframe.style.top = "0";
    iframe.style.left = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.src = pdfUrl;
    document.body.appendChild(iframe);

    const cancelButton = document.createElement("button");
    cancelButton.textContent = "Cancel";
    cancelButton.style.position = "fixed";
    cancelButton.style.top = "20px";
    cancelButton.style.right = "20px";
    cancelButton.style.padding = "10px 20px";
    cancelButton.style.backgroundColor = "#ff4d4d";
    cancelButton.style.color = "white";
    cancelButton.style.border = "none";
    cancelButton.style.borderRadius = "5px";
    cancelButton.style.cursor = "pointer";
    document.body.appendChild(cancelButton);

    iframe.onload = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    };

    cancelButton.addEventListener("click", () => {
      if (iframe.parentNode) {
        document.body.removeChild(iframe);
      }
      if (cancelButton.parentNode) {
        document.body.removeChild(cancelButton);
      }
    });
  };

  const currentFilters = {
    officeCode,
    selectedCompanyCode,
    circleCode,
    selectedDivision,
    selectedSubDivision,
  };

  useEffect(() => {
    if (lastAppliedFilters === null) {
      setIsGoDisabled(false);
      return;
    }
    const hasChanged =
      JSON.stringify(currentFilters) !== JSON.stringify(lastAppliedFilters);
    setIsGoDisabled(!hasChanged);
  }, [
    officeCode,
    selectedCompanyCode,
    circleCode,
    selectedDivision,
    selectedSubDivision,
  ]);

  const resolveApiOfficeCode = () => {
    if (selectedSubDivision) {
      return String(selectedSubDivision);
    }
    if (selectedDivision) {
      return String(selectedDivision);
    }
    if (circleCode) {
      return String(circleCode);
    }
    if (
      String(code) === "10" &&
      selectedCompanyCode &&
      selectedCompanyCode !== "10"
    ) {
      return String(selectedCompanyCode);
    }
    return String(
      resolveReportGoOfficeCode({
        code,
        officeCode,
        selectedCompanyCode,
        selectedAdminCompanyCode,
        circleCode,
        selectedDivision,
        selectedSubDivision,
      }) || officeCode || code.slice(0, 2)
    );
  };

  const handleGoClick = () => {
    const apiOfficeCode = resolveApiOfficeCode();
    const resolvedOfficeName = resolveReportOfficeName();
    setReportOfficeName(resolvedOfficeName);
    getComplaintSummary(apiOfficeCode);
    setLastAppliedFilters({
      officeCode: apiOfficeCode,
      selectedCompanyCode,
      circleCode,
      selectedDivision,
      selectedSubDivision,
    });
    setIsGoDisabled(true);
  };

  const noDataAvailable = () => {
    alert("No data available to export or print.");
  };

  const tableCaptionText = getReportTitle();
  const columnCount = TABLE_COLUMNS.length;
  const totalComplaints = tableData.reduce(
    (sum, item) => sum + (item.currentMonthComplaints || 0),
    0
  );

  return (
    <Box className="form-box rpt-page coa-status-report ucps-page nc-exceeded-summary-page">
        <div className="coa-layout">
          <Box className="application-table" style={{ marginRight: "0px", width: "100%" }}>
            <header className="coa-page-header">
            <h1 className="coa-page-title">User Complaint Processing Statistics</h1>
            </header>
            <div className="div-datagrid">
              <div className="coa-toolbar-stack">
                <Paper
                  variant="outlined"
                  className="coa-card coa-card--filters"
                  sx={{
                    p: 2,
                    width: "100%",
                    bgcolor: "#fff",
                    borderRadius: 2,
                    borderColor: "divider",
                  }}
                >
                  <div
                    className="coa-filters coa-filters--mui-grid"
                    style={{ width: "100%" }}
                  >
                    <Grid container spacing={2} alignItems="flex-end">
                      {code == 10 && (
                        <Grid item xs={12} sm={6} md={2}>
                          <Stack spacing={0.5}>
                            <Typography className="field-label">Company</Typography>
                            <select
                            className="dropdown-field ucps-filter-input"
                              value={selectedCompanyCode}
                              onChange={handleChangeCompany}
                            >
                            <option value="10">Select Company</option>
                        <option value="11">LESCO</option>
                        <option value="12">GEPCO</option>
                        <option value="13">FESCO</option>
                        <option value="14">IESCO</option>
                        <option value="15">MEPCO</option>
                        <option value="26">PESCO</option>
                        <option value="27">HAZECO</option>
                        <option value="37">HESCO</option>
                        <option value="38">SEPCO</option>
                        <option value="48">QESCO</option>
                        <option value="59">TESCO</option>
                            </select>
                          </Stack>
                        </Grid>
                      )}

                    {hierarchy <= 2 && (
                        <Grid item xs={12} sm={6} md={2}>
                          <Stack spacing={0.5}>
                            <Typography className="field-label">Circle</Typography>
                            <select
                            className="dropdown-field ucps-filter-input"
                              value={circleCode || ""}
                              onChange={handleCircleChange}
                            >
                        <option value="">All Circle</option>
                        {filteredCircles.map((circle) => (
                              <option
                                key={circle.circleCode}
                                value={circle.circleCode}
                              >
                            {circle.circleName}
                          </option>
                        ))}
                            </select>
                          </Stack>
                        </Grid>
                      )}

                    {hierarchy <= 3 && (
                        <Grid item xs={12} sm={6} md={2}>
                          <Stack spacing={0.5}>
                            <Typography className="field-label">Division</Typography>
                            <select
                            className="dropdown-field ucps-filter-input"
                              value={selectedDivision || ""}
                              onChange={handleDivisionChange}
                            disabled={!circleCode}
                            >
                        <option value="">All Division</option>
                        {filteredDivisions.map((division) => (
                              <option
                                key={division.divisionCode}
                                value={division.divisionCode}
                              >
                            {division.divisionName}
                          </option>
                        ))}
                            </select>
                          </Stack>
                        </Grid>
                      )}

                    {hierarchy <= 4 && (
                        <Grid item xs={12} sm={6} md={2}>
                          <Stack spacing={0.5}>
                            <Typography className="field-label">Sub-Division</Typography>
                            <select
                            className="dropdown-field ucps-filter-input"
                              value={selectedSubDivision || ""}
                              onChange={handleSubDivisionChange}
                            disabled={!selectedDivision}
                            >
                        <option value="">All Sub-Division</option>
                        {filteredSubDivisions.map((subDivision) => (
                              <option
                                key={subDivision.subDivisionCode}
                                value={subDivision.subDivisionCode}
                              >
                            {subDivision.subDivisionName}
                          </option>
                        ))}
                            </select>
                          </Stack>
                        </Grid>
                      )}

                      <Grid item xs={12} sm={6} md={2}>
                        <Stack spacing={0.5}>
                          <Box
                            aria-hidden
                            sx={{
                              height: "1.35rem",
                              mb: 0.5,
                              lineHeight: 1.35,
                            }}
                          />
                          <Button
                            fullWidth
                            variant="contained"
                            disableElevation
                            className="coa-go-btn"
                            onClick={handleGoClick}
                            disabled={isGoDisabled || viewBackdrop}
                          >
                            GO
                          </Button>
                        </Stack>
                      </Grid>
                    </Grid>
                  </div>

                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    alignItems="center"
                    justifyContent={{ xs: "flex-start", md: "flex-end" }}
                    spacing={1}
                    className="coa-export-strip"
                    sx={{
                      pt: 2,
                      mt: 2,
                      borderTop: "1px solid",
                      borderColor: "divider",
                      gap: 1,
                    }}
                  >
                    <ReportExportButtons
                      isEmpty={tableData.length === 0}
                      onEmpty={noDataAvailable}
                      onPdf={() => DownloadPdf(true)}
                      onExcel={exportToExcel}
                      onPrint={printPDF}
                    />
                  </Stack>
                </Paper>
              </div>

            {reportOfficeName && (
              <div className="coa-report-banner" role="status">
                {tableCaptionText}
              </div>
            )}

              <div className="table-container" style={{ marginBottom: "40px" }}>
                <div id="pdf-table">
                  <div className="coa-table-scroll">
                    <table
                    className="table coa-data-table"
                      ref={tableRef}
                      style={{
                        marginBottom: "40px",
                        width: "100%",
                        borderCollapse: "collapse",
                      }}
                    >
                      <thead
                        style={{
                          position: "sticky",
                          top: 0,
                          backgroundColor: "white",
                          zIndex: 10,
                        }}
                      >
                        <tr>
                        {TABLE_COLUMNS.map((column, index) => (
                          <th
                            key={column}
                            style={{
                              width: `${100 / columnCount}%`,
                              textAlign: index >= 3 ? "center" : "left",
                              padding: "8px 6px",
                              fontSize: "12px",
                            }}
                          >
                            {column}
                          </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                      {viewBackdrop ? (
                        <tr>
                          <td
                            colSpan={columnCount}
                            style={{ textAlign: "center", padding: "20px" }}
                          >
                            <Box
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                            >
                              <AppLoader />
                            </Box>
                          </td>
                        </tr>
                      ) : tableData.length === 0 ? (
                        <tr className="coa-empty-row">
                          <td
                            colSpan={columnCount}
                            style={{
                              textAlign: "center",
                              padding: "10px 6px",
                              fontWeight: 600,
                              color: "#991b1b",
                              backgroundColor: "#fecaca",
                              fontSize: "14px",
                              lineHeight: 1.2,
                            }}
                          >
                            No Data Found
                          </td>
                        </tr>
                      ) : (
                        <>
                          {tableData.map((item, index) => {
                            const bgColor =
                              index % 2 === 0 ? "#f8f9fa" : "#e3efff";
                            return (
                              <tr
                                key={`${item.userId}-${index}`}
                                style={{
                                  backgroundColor: bgColor,
                                  borderBottom: "1px solid #ccc",
                                }}
                              >
                                <td style={{ textAlign: "left", padding: "8px 12px", fontSize: "14px" }}>
                                  {item.userId}
                                </td>
                                <td style={{ textAlign: "left", padding: "8px 12px", fontSize: "14px" }}>
                                  {item.fullName}
                                </td>
                                <td style={{ textAlign: "left", padding: "8px 12px", fontSize: "14px" }}>
                                  {item.roleName}
                                </td>
                                <td style={{ textAlign: "center", padding: "8px", fontSize: "14px" }}>
                                  {item.circleCode}
                                </td>
                                <td style={{ textAlign: "center", padding: "8px", fontSize: "14px" }}>
                                  {item.divisionCode}
                                </td>
                                <td style={{ textAlign: "center", padding: "8px", fontSize: "14px" }}>
                                  {item.subDivisionCode}
                                </td>
                                <td style={{ textAlign: "center", padding: "8px", fontSize: "14px" }}>
                                  {item.currentMonthComplaints}
                              </td>
                              </tr>
                            );
                          })}

                          <tr className="ucps-total-row">
                            <td
                              colSpan={6}
                              style={{
                                textAlign: "left",
                                fontSize: "14px",
                                fontWeight: "bold",
                              }}
                            >
                              Total
                              </td>
                              <td
                              style={{
                                textAlign: "center",
                                fontSize: "14px",
                                fontWeight: "bold",
                              }}
                            >
                              {totalComplaints}
                              </td>
                            </tr>
                          </>
                      )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </Box>
        </div>
      </Box>
  );
};

export default UserComplaintProcessingStatistics;
